import { Query } from '@/_core/cqrs/types/queries';

export class WorkspaceSettingsQuery extends Query<WorkspaceSettingsQueryResult> {
    constructor() {
        super('WorkspaceSettingsQuery');
    }
}

export interface WorkspaceSettingsQueryResult {
    Id: number;
    Name: string;
    Description: string;
    Subdomain: string;
    CreatedAt: string;
    CreatedBy: string;
    SubsriptionManagers: string;
    WorkspaceManagers: string;
    NumberOfStoryMaps: number;
    NumberOfActiveStoryMaps: number;
    NumberOfArchivedStoryMaps: number;
    NumberOfMembers: number;
    NumberOfActiveMembers: number;
    NumberOfInvitedMembers: number;
    NumberOfLockedOutMembers: number;
    DomainAccessSettings: DomainAccessSettingsModel;
    IpAccessSettings: IpAccessSettingsModel;
    WorkspaceName: string;
    UsersWorkspaceCount: number;
    CurrentUserName: string;
    CurrentUserLoginName: string;
}

export interface DomainAccessSettingsModel {
    IsEnabled: boolean;
    RedirectToIdpWhenUnauthenticated: boolean;
    RestrictedDomains: RestrictedDomainModel[];
    LockedOutMembers: string[];
}

export interface IpAccessSettingsModel {
    IsEnabled: boolean;
    RestrictedIpAddresses: string[];
}

export interface RestrictedDomainModel {
    Id: string;
    Name: string;
    IsSsoOnlyAccess: boolean;
    SsoInfo: SsoInfoForDomain;
}

export interface SsoInfoForDomain {
    OwnerName: string;
    IsSsoOptional: boolean;
    DomainName: string;
}

// Advanced Security START

export class LockedOutWorkspaceMembersQuery extends Query<string[]> {
    constructor() {
        super('LockedOutWorkspaceMembersQuery');
    }
}

export class WorkspaceAccessHistoryQuery extends Query<WorkspaceAccessHistoryQueryResult> {
    constructor(public PageSize: number, public FilterKeyword: string, public NumberOfAlreadyVisibleItems: number) {
        super('WorkspaceAccessHistoryQuery');
    }
}

interface WorkspaceAccessHistoryQueryResult {
    WorkspaceName: string;
    Items: WorkspaceAccessHistoryItemModel[];
}

export interface WorkspaceAccessHistoryItemModel {
    Id: string;
    AccessedAt: Date;
    IsBlocked: boolean;
    UserName: string;
    UserEmail: string;
    UserIp: string;
    BlockReason: string;
}

// Audit log START

export class WorkspaceAuditLogQuery extends Query<IWorkspaceAuditLogQueryResult> {
    constructor() {
        super('WorkspaceAuditLogQuery');
    }
}

export interface IWorkspaceAuditLogQueryResult {
    WorkspaceName: string;
    ActivityGroups: IWorkspaceMemberActivityGroup[];
    Members: { [id: string]: string };
    DateOfFirstEntry: string;
    DateOfLastEntry: string;
    Entries: IWorkspaceAuditLogEntryModel[];
}

interface IWorkspaceMemberActivityGroup {
    Name: string;
    Activities: IWorkspaceMemberActivity[];
}

interface IWorkspaceMemberActivity {
    Id: number;
    Name: string;
}

interface IWorkspaceAuditLogEntryModel {
    Id: string;
    UserId: string;
    ActivityType: string;
    UserName: string;
    UserEmail: string;
    UserIp: string;
    StorymapId: string;
    ActivityAt: Date;
    Contents: string;
    HasStorymapLink: string;
}

export class WorkspaceAuditLogEntriesQuery extends Query<WorkspaceAuditLogEntry[]> {
    constructor(
        public PageSize: number,
        public NumberOfAlreadyVisibleItems: number,
        public UserIds: string[],
        public FromDate: Date,
        public ToDate: Date,
        public ActivityTypes: number[],
        public SortDirection: SortDirection
    ) {
        super('WorkspaceAuditLogEntriesQuery');
    }
}

export enum SortDirection {
    Ascending = 1,
    Descending = 2,
}

export interface WorkspaceAuditLogEntry {
    Id: string;
    UserId: string;
    ActivityType: string;
    UserName: string;
    UserEmail: string;
    UserIp: string;
    StorymapId?: string;
    ActivityAt: Date;
    Contents: string;
    HasStorymapLink: boolean;
}

export class BoardActivitiesQuery extends Query<BoardActivitiesQueryResult> {
    constructor(public PageSize: number, public NumberOfAlreadyVisibleItems: number, public AuditLogEntryId: string) {
        super('BoardActivitiesQuery');
    }
}

export class BoardActivitiesQueryResult {
    public UserName: string;
    public StorymapId: string;
    public StorymapName: string;
    public StorymapEstimationUnit: string;
    public StartDate: Date;
    public Activities: BoardUserActivity[];
}

export class BoardUserActivity {
    constructor(
        public Id: string,
        public WorkspaceId: number,
        public StorymapId: string,
        public Type: BoardUserActivityType,
        public UserId: string,
        public Username: string,
        public Datetime: string,
        public CardId: string,
        public CardName: string,
        public CardDescription: string,
        public CardNumberOfChildren: number,
        public ParentCardId: string,
        public ParentCardName: string,
        public ReleaseId: string,
        public ReleaseName: string,
        public ReleaseGoal: string,
        public ReleaseStartDate: Date,
        public ReleaseStartDateOldValue: Date,
        public ReleaseReleaseDate: Date,
        public ReleaseReleaseDateOldValue: Date,
        public ReleaseIsReleased: boolean,
        public StatusId: number,
        public StatusName: string,
        public OriginalContent: string,
        public Position: number,
        public Estimation: number,
        public EstimationUnit: string,
        public ExternalToolName: string,
        public AttachmentId: string,
        public AttachmentName: string,
        public CardColor: CardColor,
        public CardColorOldValue: CardColor,
        public CardColorLabel: string,
        public CardColorLabelOldValue: string,
        public Content: string,
        public CardPersonas: string,
        public PersonaId: string,
        public PersonaName: string,
        public PersonaGroup: string,
        public PersonaDescription: string,
        public PersonaGoals: string,
        public PersonaPictureId: string
    ) {}
}

export enum CardColor {
    Default = 0,
    Blue = 1,
    Gray = 2,
    Green = 3,
    Orange = 4,
    Pink = 5,
    Purple = 6,
    Red = 7,
    Yellow = 8,
    White = 9,
    Teal = 10,
    Indigo = 11,
    TransparentAnnotation = 99,
}

export enum BoardUserActivityType {
    DoNotSaveThis = 0,

    ActivityCreated = 10,
    ActivityDeleted = 11,
    ActivityNameChanged = 12,
    ActivityDescriptionChanged = 13,
    ActivityRestoredFromTrash = 14,
    ActivityPermanentlyDeleted = 15,
    ActivityArchived = 16,
    ActivityRestoredFromArchived = 17,

    TaskCreated = 20,
    TaskDeleted = 21,
    TaskNameChanged = 22,
    TaskDescriptionChanged = 23,
    TaskRestoredFromTrash = 24,
    TaskPermanentlyDeleted = 25,

    SubtaskCreated = 30,
    SubtaskDeleted = 31,
    SubtaskNameChanged = 32,
    SubtaskDescriptionChanged = 33,
    SubTaskStateChanged = 34,
    SubtaskUnscheduled = 35,
    SubtaskEstimationChanged = 36,
    SubtaskRestoredFromTrash = 37,
    SubtaskPermanentlyDeleted = 38,

    UnscheduledSubtaskCreated = 40,
    UnscheduledSubtaskDeleted = 41,
    UnscheduledSubtaskNameChanged = 42,
    UnscheduledSubtaskDescriptionChanged = 43,
    UnscheduledSubtaskRestoredFromTrash = 44,
    UnscheduledSubtaskPermanentlyDeleted = 45,

    ReleaseCreated = 50,
    ReleaseDeleted = 51,
    ReleaseNameChanged = 52,
    ReleaseMovedUp = 53,
    ReleaseMovedDown = 54,
    ReleaseArchived = 55,
    ReleaseReactived = 56,
    ReleaseMovedToIndex = 57,
    ReleaseGoalChanged = 58,
    ReleaseStartDateChanged = 59,
    ReleaseReleaseDateChanged = 510,
    ReleaseStatusChanged = 511,
    ReleasePushedToIntegratedTool = 512,

    MovedIntoActivities = 60,
    MovedIntoTasks = 61,
    MovedIntoSubtasks = 62,
    MovedIntoUnscheduledSubtasks = 63,

    StorymapCreatedByCopy = 70,
    StorymapCreatedFromSample = 71,

    CardsPushedToIntegrationTool = 80,
    CardPushedToIntegrationTool = 81,
    NewCardsImported = 82,

    CardAttachmentAdded = 90,
    CardAttachmentDeleted = 91,
    CardColorChanged = 92,
    CardAttachmentNameChanged = 93,

    CardUnlinkedByIntegratedTool = 100,
    CardTitleChangedByIntegratedTool = 101,
    CardDescriptionChangedByIntegratedTool = 102,
    CardStatusChangedByIntegratedTool = 103,
    CardEstimationChangedByIntegratedTool = 104,

    CardReleaseChangedByIntegratedTool = 105,
    ReleaseNameChangedByIntegratedTool = 106,
    ReleaseReleasedByIntegratedTool = 107,
    ReleaseUnreleasedByIntegratedTool = 108,
    ReleaseArchivedByIntegratedTool = 109,
    ReleaseReactivatedByIntegratedTool = 1010,
    ReleaseGoalChangedByIntegratedTool = 1011,
    ReleaseStartDateChangedByIntegratedTool = 1012,
    ReleaseReleaseDateChangedByIntegratedTool = 1013,
    ReleaseUnlinkedByIntegratedTool = 1014,
    CardReleaseRemovedByIntegratedTool = 1015,

    CardCopiedToClipboard = 110,
    CardPastedFromClipboard = 111,

    ActivityPasted = 112,
    TaskPasted = 113,
    SubtaskPasted = 114,
    UnscheduledSubtaskPasted = 115,

    CardCommentAdded = 116,
    CardCommentEdited = 117,
    CardCommentRemoved = 118,

    NotificationDismissed = 119,
    EverybodyMentionedInComment = 120,

    PersonasAssignedToActivity = 121,
    ActivityPersonasReordered = 122,
    ActivityPersonasUpdated = 123,
    PersonasUnassignedFromActivity = 124,
    PersonaAdded = 125,
    PersonaDeleted = 126,
    PersonaNameChanged = 127,
    PersonaDescriptionChanged = 128,
    PersonaGoalsChanged = 129,
    PersonaGroupChanged = 130,
    PersonaPictureChanged = 131,
    PersonaAddedAndAssigned = 132,
    PersonaUserImageAdded = 133,
    PersonaUserImageDeleted = 134,
    PersonaCustomFieldChanged = 135,
    PersonaPersonalityFieldChanged = 136,

    AboutBoardTextChanged = 140,

    CardAnnotationsUpdated = 150,
    CardAnnotationsReordered = 151,
    CardAnnotationsAdded = 152,
    CardAnnotationsRemoved = 153,

    AnnotationNameChanged = 160,
    AnnotationCreated = 161,
    AnnotationDeleted = 162,
    AnnotationIconChanged = 163,
    AnnotationEdited = 164,
    AnnotationMoved = 165,

    BoardCommentAdded = 171,
    BoardCommentEdited = 172,
    BoardCommentRemoved = 173,
    BoardAttachmentAdded = 174,
    BoardAttachmentDeleted = 175,
    BoardAttachmentNameChanged = 176,
    BoardWebLinkAdded = 177,
    BoardWebLinkDeleted = 178,
    BoardWebLinkNameChanged = 179,

    CardWebLinkAdded = 180,
    CardWebLinkNameChanged = 181,
    CardWebLinkDeleted = 182,

    IdeaPanelFiltersChanged = 200,

    IdeaIsProcessedChanged = 210,

    FeedbackIsProcessedChanged = 220,

    SubtaskStatusAdded = 230,
    SubtaskStatusRenamed = 231,
    SubtaskStatusMovedUp = 232,
    SubtaskStatusMovedDown = 233,
    SubtaskStatusColorChanged = 234,
    SubtaskStatusDeleted = 235,

    AssigneeChanged = 240,

    PriorityChanged = 250,
    BusinessValueChanged = 251,
    EffortChanged = 252,

    PrioFrameworkChanged = 270,

    CardUpdatedByMcp = 280,

    ReleaseUpdatedByMcp = 281,
    PersonaUpdatedByMcp = 282,
    AnnotationUpdatedByMcp = 283,
    AnnotationArchivedByMcp = 284,
    AnnotationRestoredByMcp = 285,
}
